import { useRef, useState, useCallback } from 'react';
import { useRegisterUnsavedChanges } from './useUnsavedChanges';

export interface DirtyTrackerOptions {
    /**
     * When `true` (default), this form's `isDirty` signal is auto-registered
     * into the global `<UnsavedChangesProvider>`, so navigating away (or
     * closing the tab) while dirty triggers the unsaved-changes warning — with
     * zero extra wiring. Set to `false` for forms whose dirtiness should not
     * block navigation (rare). No-op when no provider is mounted.
     */
    guardNavigation?: boolean;
    /**
     * While `true`, the baseline tracks the current value every render, so
     * `isDirty` stays `false` no matter how `value` changes. Use it to suppress
     * the dirty signal until the user actually interacts — e.g. a modal form
     * that programmatically prefills its fields *after* opening would otherwise
     * read as dirty the instant the prefill lands. Flip it to `false` on the
     * first real edit to freeze the baseline at the prefilled value.
     */
    pinned?: boolean;
}

export interface DirtyTracker {
    /** True when `value` differs from the captured baseline. Computed
     *  synchronously in render — there is no 1-render lag between a state
     *  change and the dirty signal. */
    isDirty: boolean;
    /** Reset the baseline to the current `value`. Call after a successful save. */
    markClean: () => void;
    /** Reset the baseline to a freshly received value (e.g. after refetching from the server). */
    reset: (next: unknown) => void;
}

/**
 * Tracks whether a form's `value` has diverged from its initial baseline.
 *
 * Pattern: serialize `value` with JSON.stringify and compare against a ref
 * captured on first render. The comparison runs **synchronously during
 * render**, so the very first keystroke (or click, color pick, photo
 * upload, …) flips `isDirty` to true on the same render. Cheap,
 * deterministic enough for typical form state (V8 preserves insertion
 * order for plain objects), and zero dependencies.
 *
 * Limitations: does NOT track non-serializable values (`File`, `Map`, `Set`,
 * functions). For forms that include those, compose manually:
 *
 *   const formDirty = useDirtyTracker(editableX);
 *   const isDirty = formDirty.isDirty
 *                || pendingPhotos.length > 0
 *                || deletedIds.length > 0
 *                || childRowsRef.current?.some(r => r.isDirty());
 *
 * After `onSave` resolves, call `markClean()` so the save button greys back out.
 *
 * `markClean` and `reset` are stable references — safe to put in `useEffect` deps.
 *
 * Nullish = inactive: passing `null`/`undefined` (the common
 * `useDirtyTracker(modalOpen ? {...} : null)` modal pattern) reads as **clean**,
 * and the baseline is adopted from the first value once it becomes non-nullish —
 * so reopening a modal or loading a record never reports a phantom change. (Older
 * code worked around this with `pinned` + a manual "touched" flag; that's no
 * longer needed for the open/closed toggle.)
 *
 * @param value Anything serializable. Pass the entire editable form state.
 * @param options See {@link DirtyTrackerOptions}.
 */
export function useDirtyTracker<T>(value: T, options?: DirtyTrackerOptions): DirtyTracker {
    const baselineRef = useRef<string>(JSON.stringify(value));
    const valueRef = useRef<T>(value);
    valueRef.current = value;
    // Whether the PREVIOUS render's value was nullish — lets us detect the
    // "inactive → active" transition (e.g. a modal toggling `open ? {...} : null`).
    const wasNullishRef = useRef<boolean>(value == null);
    // Forces a re-render when `markClean`/`reset` mutate the baseline ref
    // without any other state changing. Without this the `isDirty` signal
    // would stay `true` after a successful save until the next unrelated
    // re-render.
    const [, forceUpdate] = useState(0);

    const next = JSON.stringify(value);
    const isNullish = value == null;
    // Re-baseline in three cases so the form never reads as dirty without a
    // real edit:
    //   1. `pinned` — caller explicitly suppresses dirtiness (prefills land
    //      after open).
    //   2. value is nullish — the form is "inactive" (a closed modal's
    //      `open ? {...} : null`, an unloaded record). Glue the baseline to it.
    //   3. the value just went nullish → non-nullish — the form reopened/loaded;
    //      adopt the first active value as the fresh baseline.
    // Cases 2–3 fix the footgun where `useDirtyTracker(open ? {...} : null)`
    // captured its baseline as "null" while closed, so the first non-null render
    // flipped isDirty to true with no edit ("modal opens already dirty"). The
    // first real edit (same active object, no longer a transition) measures from
    // that adopted baseline.
    if (options?.pinned || isNullish || wasNullishRef.current) {
        baselineRef.current = next;
    }
    wasNullishRef.current = isNullish;
    const isDirty = next !== baselineRef.current;

    const markClean = useCallback(() => {
        baselineRef.current = JSON.stringify(valueRef.current);
        forceUpdate(n => n + 1);
    }, []);

    const reset = useCallback((nextValue: unknown) => {
        baselineRef.current = JSON.stringify(nextValue);
        forceUpdate(n => n + 1);
    }, []);

    // Auto-arm the global navigation guard while this form is dirty. No-op when
    // no <UnsavedChangesProvider> is mounted, so existing call-sites and the
    // forked web repo are unaffected.
    useRegisterUnsavedChanges(isDirty, options?.guardNavigation ?? true);

    return { isDirty, markClean, reset };
}
